import axios, { AxiosResponse } from "axios";
import firebase from "firebase/app";
import "firebase/auth";
import { Response } from "@/shared/models/Response";
import { GOOGLE_PROVIDER_PARAMS } from "../constants/LoginConstants";
import { FORM_MESSAGES, LOCAL_STORAGE } from "@/shared/constants/FormConstants";
import store from "@/store";
import Snackbar from "@/shared/components/Snackbar/Snackbar";
import { Claims } from "../models/Claims";
import { ClaimsWpp } from "../models/ClaimsWpp";
import { ClaimsProps } from "../models/ClaimsProps";
import { User } from "../models/User";
import { ConfigSearcher } from "@/shared/models/ConfigSearcher";

export class AuthService {
  private baseUrl: string;

  constructor() {
    this.baseUrl = `${process.env.VUE_APP_AUTH_API}/auth`;
  }

  async sendOtp(document: string) {
    const serviceURL = `${this.baseUrl}/send-otp`;
    const response = await axios.get(serviceURL, {
      params: { document }
    });
    return response.data;
  }

  async validateOtp(document: string, codeOtp: number, codeGoogle: string) {
    const serviceURL = `${this.baseUrl}/validate-otp`;
    const response = await axios.get(serviceURL, {
      params: { document, codeOtp, codeGoogle }
    });
    return response.data;
  }

  async changePasswordUser(userId: string, password: string) {
    const request = await axios.put(
      `${this.baseUrl}/change-password/${userId}`,
      {
        password: password
      }
    );
    return request.data;
  }

  async login(user: string, pass: string): Promise<Response<Claims>> {
    const res: AxiosResponse<Response<Claims>> = await axios.post(
      `${this.baseUrl}/login-by-credentials`,
      {
        user: user,
        password: pass
      }
    );
    return res.data;
  }

  async loginByToken(userToken: string) {
    return await firebase.auth().signInWithCustomToken(userToken);
  }

  async loginWithGoogle(): Promise<firebase.auth.UserCredential> {
    const googleProvider = new firebase.auth.GoogleAuthProvider();
    googleProvider.setCustomParameters(GOOGLE_PROVIDER_PARAMS);

    return await firebase.auth().signInWithPopup(googleProvider);
  }

  async validateUser(id: string, email: string): Promise<Response<Claims>> {
    return (await axios.get(`${this.baseUrl}/login-by-provider/${id}/${email}`))
      .data;
  }

  async sendWhatsapp(id: string): Promise<Response<ClaimsWpp>> {
    return (await axios.post(`${this.baseUrl}/register`, { documento: id }))
      .data;
  }

  async updateToken(token: string): Promise<Response<string>> {
    return (await axios.post(`${this.baseUrl}/update-token/${token}`)).data;
  }

  async decodeToken(token: string, userId: string): Promise<ClaimsProps> {
    return (await axios.get(`${this.baseUrl}/decode-token/${token}/${userId}`))
      .data;
  }

  async registerUser(data: User): Promise<Response<ClaimsProps>> {
    return (await axios.post(`${this.baseUrl}/register-login`, data)).data;
  }

  invalidEmail() {
    const currentUser = firebase.auth().currentUser;

    if (currentUser !== null) {
      currentUser.delete();
    }
  }

  setTokenAndUser(token: string, user: string | undefined) {
    localStorage.setItem("TOKEN", token);
    if (user) {
      localStorage.setItem("UID", user);
      store.commit("setUserId", user);
    }
  }

  async setClaims(
    user: firebase.User | null,
    views: string[],
    access: string[],
    searcher?: ConfigSearcher[]
  ) {
    try {
      const userClaims = await user?.getIdTokenResult(true);
      localStorage.setItem(
        LOCAL_STORAGE.CLIENT_ID,
        userClaims?.claims.clientId
      );
      localStorage.setItem(
        LOCAL_STORAGE.SEARCHER,
        JSON.stringify(searcher ?? [])
      );
      localStorage.setItem(LOCAL_STORAGE.VIEWS, JSON.stringify(views));
      localStorage.setItem(LOCAL_STORAGE.ROLE, userClaims?.claims.role);
      localStorage.setItem(
        LOCAL_STORAGE.USER_NAME,
        userClaims?.claims.userName
      );
      localStorage.setItem(LOCAL_STORAGE.AVATAR, userClaims?.claims.avatar);
      localStorage.setItem(LOCAL_STORAGE.ACCESS, JSON.stringify(access));
      store.commit("setRole", userClaims?.claims.role);
      store.commit("setUserName", userClaims?.claims.userName);
      store.commit("setAvatar", userClaims?.claims.avatar);
    } catch (error) {
      Snackbar.popError(FORM_MESSAGES.REQUEST_FAILED);
    }
  }

  async specialistSignature(
    user: string,
    password: string
  ): Promise<Response<string>> {
    return (
      await axios.post(`${this.baseUrl}/specialist-signature`, {
        user,
        password
      })
    ).data;
  }
}
